import { motion } from "motion/react";

import { mcpClientLogo } from "./Mcp/mcpClientLogos";
import { MCP_CLIENTS, type McpClientSlug } from "./Mcp/mcpClients";
import { MCP_DOCS_URL, mcpClientDocsUrl } from "./Mcp/mcpServer";
import { useReducedMotion } from "./ui/use-reduced-motion";

// The landing strip is a curated marketing lineup, not the MCP docs index:
// it shows the coding agents our audience already uses, in this order.
// Names and marks come from MCP_CLIENTS. Claude Code deliberately uses the
// Claude spark mark — it reads better at lockup size than the Claude Code
// terminal mark.
const STRIP_ENTRIES: { slug: McpClientSlug; logoSlug?: McpClientSlug }[] = [
  { slug: "cursor" },
  { slug: "claude-code", logoSlug: "claude" },
  { slug: "windsurf" },
  { slug: "codex" },
];

const tools = STRIP_ENTRIES.flatMap(({ slug, logoSlug }) => {
  const client = MCP_CLIENTS.find((c) => c.slug === slug);
  const Logo = mcpClientLogo(logoSlug ?? slug);
  return client && Logo
    ? [{ name: client.name, Logo, href: mcpClientDocsUrl(slug) }]
    : [];
});

export default function AgentCompatibility() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="relative w-full px-4 py-12 md:px-8 lg:px-16 lg:py-28">
      <motion.div
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }}
        className="mx-auto max-w-[1600px] text-center"
      >
        <p className="mb-8 text-sm tracking-widest text-gray-400 uppercase">
          Works with the agents you already use
        </p>

        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 text-white/60 lg:gap-x-14">
          {tools.map(({ name, Logo, href }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener"
              aria-label={`Set up Flowstep MCP for ${name}`}
              className="flex items-center gap-3 transition-colors hover:text-white/90"
            >
              <Logo className="h-6 w-auto" aria-hidden />
              <span className="text-lg font-medium">{name}</span>
            </a>
          ))}
        </div>

        <a
          href={MCP_DOCS_URL}
          target="_blank"
          rel="noopener"
          className="mt-8 inline-block text-sm text-[#6b96ff] transition-all hover:font-medium"
        >
          Connect any agent over MCP →
        </a>
      </motion.div>
    </section>
  );
}
